/**
 * What the next dig will ask, shown where the room's own prompt is. A report's
 * Dig button STAGES its directive here instead of starting at once, so the
 * change is visible and editable before it costs anything. Dig wider clears
 * it; Dig deeper (here or in the run control) starts it.
 *
 * TWO PLACES. Typing a directive of your own lives in the room brief (`inline`),
 * with the focus beside it. Above the board it is a strip only while something
 * is actually staged — a report's Dig button put it there, and a dig about to
 * spend is worth seeing without opening anything. An empty "next dig wakes
 * everyone" strip was a line of screen saying nothing was happening.
 */
import { useEffect, useRef, useState } from 'react';
import { scopeLabel, whoLabel, type StagedDig } from './reportsView';

/** How long typing pauses before the parent hears about it. */
const COMMIT_MS = 250;

export default function NextDig({
  staged,
  onChange,
  onStart,
  onClear,
  digging,
  budgetLabel,
  inline = false,
}: {
  staged: StagedDig | null;
  onChange: (next: StagedDig) => void;
  /** Start the dig with the directive as typed — passed rather than read from
   *  the staged state, which may be a few hundred ms behind the box. */
  onStart: (directive: string) => void;
  onClear: () => void;
  digging: boolean;
  budgetLabel: string;
  /** In the room brief: always open, no strip of its own. */
  inline?: boolean;
}) {
  const empty: StagedDig = { directive: '', archetype: null, item_id: null, scope: null, intent: 'DEEPER', source: 'typed here' };
  const s = staged ?? empty;
  // THE KEYSTROKE STAYS HERE. `staged` lives on the room page, and every
  // character used to go straight into it — which re-rendered the whole room
  // (the rail with its facets, the report, the feed) per keystroke, and typing
  // a sentence felt like wading. The box owns a draft; the room hears about
  // it when typing pauses, on blur, and — synchronously — when a dig starts.
  const [draft, setDraft] = useState(s.directive);
  const committed = useRef(s.directive);
  useEffect(() => {
    // A Dig button staged a directive from outside: adopt it. The debounce
    // echo of our own commit equals the draft already, so this is a no-op then.
    if (s.directive !== committed.current) {
      committed.current = s.directive;
      setDraft(s.directive);
    }
  }, [s.directive]);
  const commit = (value: string) => {
    if (value === committed.current) return;
    committed.current = value;
    onChange({ ...s, directive: value, source: staged?.source ?? 'typed here' });
  };
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  const onType = (value: string) => {
    setDraft(value);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => commit(value), COMMIT_MS);
  };
  const flush = () => {
    if (timer.current) clearTimeout(timer.current);
    commit(draft);
  };
  const has = Boolean(draft.trim() || s.scope || s.archetype);
  // One quiet line until there is something to say or you ask to type.
  const [typing, setTyping] = useState(false);
  const openBox = inline || has || typing;
  return (
    <section
      className={
        inline
          ? 'text-xs'
          : `shrink-0 rounded-lg border px-3 text-xs ${
              has ? 'border-teal-300 dark:border-teal-700 bg-teal-50/50 dark:bg-teal-900/10 py-2' : 'border-dashed border-gray-300 dark:border-gray-600 py-1'
            }`
      }
      data-testid="next-dig"
    >
      <div className="flex items-center gap-2 flex-wrap">
        {!inline && <span className="text-[11px] uppercase tracking-wide text-gray-400">Next dig</span>}
        <span className="text-gray-600 dark:text-gray-300">
          wakes <strong className="font-medium">{whoLabel(s.archetype)}</strong>
          {s.item_id && <> · on one item</>}
          {s.scope && (
            <>
              {' '}
              · within <strong className="font-medium">{scopeLabel(s.scope)}</strong>
            </>
          )}{' '}
          · {budgetLabel}
        </span>
        {has && <span className="text-[11px] text-gray-400">from {s.source}</span>}
        {draft.trim() && (
          <span
            className="text-[11px] text-violet-600 dark:text-violet-400"
            title="The sorter reads the setup against what you asked and may add a tag group or propose taking one away"
          >
            · may change the frame
          </span>
        )}
        {!openBox && (
          <button type="button" onClick={() => setTyping(true)} className="text-teal-700 dark:text-teal-300 hover:underline" data-testid="chase">
            chase something…
          </button>
        )}
        <span className="ml-auto flex items-center gap-1.5">
          {has && (
            <button
              type="button"
              onClick={onClear}
              className="px-1.5 py-0.5 rounded border border-gray-300 dark:border-gray-600 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Clear
            </button>
          )}
          {openBox && (
            <button
              type="button"
              onClick={() => {
                flush();
                onStart(draft);
              }}
              disabled={digging || !has}
              title={digging ? 'A dig is going' : !has ? 'Type what to chase, or press a Dig button on a report' : 'Start this dig'}
              className="px-2 py-0.5 rounded bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
              data-testid="start-staged"
            >
              {digging ? 'a dig is going' : 'Start this dig'}
            </button>
          )}
        </span>
      </div>
      {openBox && (
        <textarea
          aria-label="What should the next dig chase?"
          value={draft}
          onChange={e => onType(e.target.value)}
          onBlur={flush}
          rows={inline ? 3 : draft.length > 160 ? 3 : 1}
          placeholder="What should the next dig chase? Leave empty for an ordinary dig. A report's Dig button fills this in."
          className="mt-1.5 w-full border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-teal-500"
          autoFocus={typing && !has}
        />
      )}
    </section>
  );
}

